import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Activity, RefreshCw, Loader2, TrendingUp, ShoppingCart,
  Users, Server, Clock, Zap, Database, AlertTriangle, Shield,
} from "lucide-react";

function MetricCard({ label, value, sub, icon: Icon, color = "blue" }) {
  const colors = {
    blue: "bg-blue-500/10 text-blue-600",
    green: "bg-emerald-500/10 text-emerald-600",
    amber: "bg-amber-500/10 text-amber-600",
    red: "bg-red-500/10 text-red-600",
    purple: "bg-purple-500/10 text-purple-600",
  };
  return (
    <Card data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wide">{label}</p>
            <p className="text-xl font-black tracking-tight">{value}</p>
            {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function SystemMetricsTab({ adminAxios }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAxios.get("/system-metrics");
      setMetrics(res.data);
    } catch {
      toast.error("Erro ao carregar metricas");
    }
    setLoading(false);
  }, [adminAxios]);

  useEffect(() => { fetch(); const i = setInterval(fetch, 30000); return () => clearInterval(i); }, [fetch]);

  if (loading && !metrics) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const m = metrics || {};
  const r = m.restaurants || {};
  const o = m.orders || {};
  const p = m.performance || {};
  const infra = m.infrastructure || {};

  return (
    <div className="space-y-4" data-testid="metrics-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-purple-500/10"><Activity className="w-6 h-6 text-purple-600" /></div>
          <div>
            <h2 className="font-black text-lg tracking-tight">Metricas Operacionais</h2>
            <p className="text-[10px] text-muted-foreground">Atualiza a cada 30s</p>
          </div>
        </div>
        <Button size="sm" variant="ghost" className="gap-1 h-7 text-[10px]" onClick={fetch} data-testid="refresh-metrics">
          <RefreshCw className="w-3 h-3" />Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Restaurantes Ativos" value={r.active || 0} sub={`${r.total || 0} total | ${r.suspended || 0} suspensos`} icon={TrendingUp} color="green" />
        <MetricCard label="Pedidos Hoje" value={(o.today || 0).toLocaleString()} sub={`${o.per_minute || 0}/min | ${o.last_5min || 0} ultimos 5min`} icon={ShoppingCart} color="blue" />
        <MetricCard label="Latencia Media" value={`${p.avg_latency_ms || 0}ms`} sub={p.error_rate_pct > 0 ? `Error rate: ${p.error_rate_pct}%` : "Sem erros"} icon={Zap} color={p.avg_latency_ms > 800 ? "red" : p.avg_latency_ms > 300 ? "amber" : "green"} />
        <MetricCard label="Fila Redis" value={infra.queue_size || 0} sub={`${infra.mongo_connections || 0} conexoes MongoDB`} icon={Server} color={infra.queue_size > 100 ? "red" : "blue"} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Usuarios" value={(m.users?.total || 0).toLocaleString()} icon={Users} color="purple" />
        <MetricCard label="Receita Hoje" value={`${(m.revenue?.today_eur || 0).toFixed(2)} EUR`} icon={TrendingUp} color="green" />
        <MetricCard label="Jarvis Incidentes" value={m.jarvis?.incidents_today || 0} sub="Hoje" icon={Shield} color={m.jarvis?.incidents_today > 10 ? "red" : "green"} />
        <MetricCard label="Error Rate" value={`${p.error_rate_pct || 0}%`} sub={p.error_rate_pct > 3 ? "Acima do limite" : "Normal"} icon={AlertTriangle} color={p.error_rate_pct > 7 ? "red" : p.error_rate_pct > 3 ? "amber" : "green"} />
      </div>

      {m.timestamp && (
        <p className="text-[9px] text-muted-foreground text-right">Ultima atualizacao: {m.timestamp?.slice(11, 19)}</p>
      )}
    </div>
  );
}
